import { IPaginationResponse } from '@wyz/types';
import { Button, Col, Datatable, Row } from '@wyz/ui';
import { invariant, QueryKey } from '@wyz/utils';
import { NumberField } from '~/common/components';
import { Trans, useLocale } from '~/common/context';
import { CONTAINER_TYPES } from '~/common/schemas';
import { PurchaseQueryKeys } from '~/purchase/api';
import { usePurchaseContext } from '~/purchase/context';
import { ContainerSearchHelpers } from '~/purchase/helpers';
import {
  IContainerOrderSearchQs,
  IContainerSearchResult,
  IContainerSearchTyreFilter,
} from '~/purchase/types';

const toPaginationResponse = (
  data: IContainerSearchResult['offers'],
): IPaginationResponse<IContainerSearchResult['offers'][0]> => {
  return {
    data: data,
    meta: {
      currentPage: 1,
      from: 1,
      hasMore: false,
      perPage: 25,
      to: 1,
      total: 1,
    },
    status: 'SUCCESS',
  };
};
type Props = {
  offers: IContainerSearchResult['offers'];
  summary: IContainerSearchResult['summary'];
  filter?: IContainerSearchTyreFilter;
  searchParams: IContainerOrderSearchQs;
  additionalQueryKeys: QueryKey;
  isSubmitting: boolean;
  isValid: boolean;
};

const ContainerSearchOfferTable = ({
  offers,
  summary,
  filter,
  searchParams,
  additionalQueryKeys,
  isSubmitting,
  isValid,
}: Props) => {
  const locale = useLocale();

  const purchaseContext = usePurchaseContext();
  const targetCustomer = purchaseContext.state.targetCustomer;
  invariant(targetCustomer);

  return (
    <>
      <div>
        <Datatable.Container<IContainerSearchResult['offers'][0]>
          columns={[
            {
              field: 'tyre.brand.name',
              align: 'center',
              headerAlign: 'center',
              headerName: <Trans i18nKey='app.common.product.brand' />,
              renderCell: ({ row }) => <div>{row.tyre.brand.name}</div>,
            },
            {
              field: 'tyre.ean',
              align: 'center',
              headerAlign: 'center',
              headerName: <Trans i18nKey='app.common.product.ean' />,
            },
            {
              field: 'tyre.dimension.fullSize',
              align: 'center',
              headerAlign: 'center',
              headerName: <Trans i18nKey='app.common.product.dimension' />,
            },
            {
              field: 'tyre.profile.design1',
              align: 'center',
              headerAlign: 'center',
              headerName: <Trans i18nKey='app.common.product.profile' />,
            },
            {
              field: 'tyre.season.name',
              align: 'center',
              headerAlign: 'center',
              headerName: <Trans i18nKey='app.common.product.season' />,
              renderCell: ({ row }) => (
                <div>
                  <Trans i18nKey={row.tyre.season.i18nKey} />
                </div>
              ),
            },
            {
              field: 'netPrice',
              align: 'center',
              headerAlign: 'center',
              headerName: <Trans i18nKey='app.common.unit_price_with_tax' />,
              renderCell: ({ row }) => (
                <div>
                  {locale.formatters.money(row.netPrice, {
                    currency: summary.currency,
                  })}
                </div>
              ),
            },
            {
              field: 'numberOfTyres',
              align: 'center',
              headerAlign: 'center',
              headerName: (
                <Trans
                  i18nKey={
                    searchParams.containerType === CONTAINER_TYPES.CONTAINER
                      ? 'app.container.order.search.number_of_tyres_per_container'
                      : 'app.container.order.search.number_of_tyres_per_truck'
                  }
                />
              ),
              renderCell: ({ row }) => (
                <div>{locale.formatters.quantity(row.numberOfTyres)}</div>
              ),
            },
            {
              field: 'quantity',
              align: 'center',
              headerAlign: 'center',
              headerName: <Trans i18nKey='app.common.quantity' />,
              renderCell: ({ row }) => {
                return (
                  <div className='d-flex justify-content-center'>
                    <NumberField
                      name={`${row.tyre.ean}.quantity`}
                      min={0}
                      max={Infinity}
                      size='sm'
                      defaultValue={0}
                    />
                  </div>
                );
              },
            },
          ]}
          fetcher={() =>
            Promise.resolve(
              toPaginationResponse(
                ContainerSearchHelpers.filterOffers(offers, filter),
              ),
            )
          }
          queryKey={[
            ...PurchaseQueryKeys.containerOrder.searchResults(
              targetCustomer.id,
              {
                supplier: searchParams.supplier,
                containerType: searchParams.containerType,
                quantity: searchParams.quantity,
              },
            ),
            ...additionalQueryKeys,
            filter,
          ]}
        >
          <>
            <Datatable.Table bordered striped={false} />
          </>
        </Datatable.Container>
      </div>
      <Row>
        <Col className='d-flex justify-content-end space-x-2 mt-4'>
          <Button
            id='submit-order'
            variant='primary'
            size='md'
            type='submit'
            isLoading={isSubmitting}
            disabled={!isValid}
          >
            <Trans i18nKey='app.common.buttons.validate' />
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default ContainerSearchOfferTable;
